/**
 * Trial state helpers — single source of truth for "is this user on the
 * 7-day Strength trial, and how much time is left". Mirrors the contract
 * documented by the sibling team in /app/docs/TRIAL_API.md.
 *
 * Sibling user-object contract (from /api/auth/me):
 *   membership_status:    "trial" | "active" | "inactive"
 *   membership_plan:      "Momentum" | "Strength" | "Elite Coaching" | null
 *   membership_billing:   "family_seat" | "monthly" | "annual" | null
 *   trial_started_at:     ISO-8601 string | null
 *   trial_ends_at:        ISO-8601 string | null   (+7 days from start)
 *   trial_expired_at:     ISO-8601 string | null   (set when status flips)
 */

/** Returns null when the user is NOT on a trial (active sub, expired,
 *  family seat, or no user). When on trial returns the full state for
 *  banner/dialog rendering. */
export function getTrialState(user) {
  if (!user) return null;
  // Family seats inherit the parent plan instantly — they never see a
  // trial countdown.
  if (user.membership_billing === "family_seat") return null;
  if (user.membership_status !== "trial") return null;
  if (!user.trial_ends_at) return null;

  const endsAt = new Date(user.trial_ends_at);
  const msLeft = endsAt.getTime() - Date.now();
  if (msLeft <= 0) {
    return {
      expired: true,
      daysLeft: 0,
      hoursLeft: 0,
      endsAt,
      severity: "expired",
    };
  }

  const hoursLeft = Math.floor(msLeft / 3_600_000);
  const daysLeft = Math.floor(msLeft / 86_400_000);

  // Severity steps mirror the messaging tree in TRIAL_API.md.
  // <24h is red, 3-2 days is amber, 4+ days is calm sky.
  let severity = "info";
  if (hoursLeft < 24) severity = "urgent";
  else if (daysLeft <= 3) severity = "warning";

  return { expired: false, daysLeft, hoursLeft, endsAt, severity };
}

/** Headline + CTA per the messaging-tree the user supplied. */
export function getTrialCopy(state) {
  if (!state) return null;
  if (state.expired) {
    return {
      headline: "Your trial ended. Pick a plan to get your tools back.",
      cta: "Pick a plan",
    };
  }
  if (state.hoursLeft < 24) {
    return {
      headline: `Last day! Pick a plan or your AI tools turn off in ${state.hoursLeft}h.`,
      cta: "Pick a plan",
    };
  }
  if (state.daysLeft <= 1) {
    return {
      headline: "Last day! Pick a plan or your AI tools turn off tomorrow.",
      cta: "Pick a plan",
    };
  }
  if (state.daysLeft <= 3) {
    return {
      headline: `Your trial ends in ${state.daysLeft} days. Upgrade now and never lose access.`,
      cta: "Pick a plan",
    };
  }
  if (state.daysLeft <= 5) {
    return {
      headline: "Loving the AI scanner? Lock it in for $50/mo.",
      cta: "See plans",
    };
  }
  return {
    headline: `Welcome — you've got ${state.daysLeft} days of Strength on us.`,
    cta: "Tour features",
  };
}
